// Product sheets for quick view, keyed by URL. Shared so a sheet is fetched once
// per page view: prefetched on touch/hover, reused when the drawer is reopened.
const quickViewRequests = new Map();

function fetchQuickView(productUrl) {
  const sectionUrl = `${productUrl.split('?')[0]}?view=modal`;

  if (!quickViewRequests.has(sectionUrl)) {
    const request = fetch(sectionUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
        return response.text();
      })
      .catch(error => {
        quickViewRequests.delete(sectionUrl);
        throw error;
      });

    quickViewRequests.set(sectionUrl, request);
  }

  return quickViewRequests.get(sectionUrl);
}

class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }

  // Cards live inside product sliders (overflow: auto). iOS Safari clips the
  // fixed-position drawer to the slider's box, so the sheet shows up trapped
  // in the slider with no close or add-to-cart button. Move the drawer to
  // <body> while it is open and put it back in its card once it has closed.
  openMenuDrawer(summaryElement) {
    const scrollY = window.scrollY;

    if (this.parentElement !== document.body) {
      this.placeholder = document.createComment('quick-view-drawer');
      this.before(this.placeholder);
      document.body.appendChild(this);
    }

    super.openMenuDrawer(summaryElement);

    // Opening focuses the summary, which now sits at the top of <body>;
    // keep the page where the customer was.
    if (window.scrollY !== scrollY) window.scrollTo(0, scrollY);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    if (event === undefined) return;

    setTimeout(() => this.restorePosition(), 450);
  }

  restorePosition() {
    if (!this.placeholder || this.mainDetailsToggle.hasAttribute('open')) return;

    this.placeholder.replaceWith(this);
    this.placeholder = null;
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    // Start loading the product sheet as soon as the finger or pointer lands,
    // so the drawer usually has it by the time it opens.
    const prefetch = () => {
      if (this.dataset.productUrl) fetchQuickView(this.dataset.productUrl).catch(() => {});
    };
    this.addEventListener('touchstart', prefetch, { passive: true, once: true });
    this.addEventListener('pointerenter', prefetch, { once: true });

    this.addEventListener('click', () => {
      // Remembered because the drawer leaves the card while it is open.
      this.drawer = this.drawer || this.closest('.card-wrapper').querySelector('quick-view-drawer');
      const drawer = this.drawer;
      if (drawer) {
        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Runs again each time the drawer moves to <body> and back; one observer is enough.
    if (this.intersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    this.intersectionObserver.observe(this);
  }

  isOpen() {
    const details = this.closest('details');
    return !details || details.hasAttribute('open');
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    if (drawerContent.querySelector('product-info, .quick-view__loading')) return;

    const productUrl = this.dataset.productUrl;
    this.showLoading(drawerContent);

    // Give the opening animation time to play, but never leave the customer
    // waiting on a blank sheet: after 10s send them to the product page instead.
    const minimumDelay = new Promise(resolve => setTimeout(resolve, 250));
    const timeout = new Promise((_resolve, reject) => {
      setTimeout(() => reject(new Error('Quick view timed out')), 10000);
    });

    Promise.race([Promise.all([fetchQuickView(productUrl), minimumDelay]), timeout])
      .then(([responseText]) => {
        if (!this.isOpen()) return;

        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector(selector);
        if (!productElement) throw new Error('Quick view content not found');

        this.setInnerHTML(drawerContent, productElement.innerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: productUrl
            }
          }));
        }, 300);
      })
      .catch(e => {
        console.error(e);
        if (this.isOpen()) window.location.href = productUrl;
      });
  }

  showLoading(drawerContent) {
    drawerContent.innerHTML = `
      <drawer-close-button class="quick-view__close">
        <span class="icon icon-plus-alt"></span>
      </drawer-close-button>
      <div class="quick-view__loading" role="status" aria-live="polite">
        <span class="quick-view__spinner"></span>
      </div>`;
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);
